import React from 'react';
import { PERMISSION_GROUPS, Permission } from '../lib/permissions';

interface PermissionCheckboxesProps {
  selectedPermissions: string[];
  onChange: (permissions: string[]) => void;
}

const PermissionCheckboxes: React.FC<PermissionCheckboxesProps> = ({
  selectedPermissions,
  onChange,
}) => {
  const handlePermissionToggle = (permissionId: string) => {
    const isSelected = selectedPermissions.includes(permissionId);
    
    if (isSelected) {
      onChange(selectedPermissions.filter(id => id !== permissionId));
    } else {
      onChange([...selectedPermissions, permissionId]);
    }
  };

  const handleGroupToggle = (groupPermissions: Permission[]) => {
    const allGroupPermissionIds = groupPermissions.flatMap(p => {
      const ids = [p.id];
      if (p.children) {
        ids.push(...p.children.map(c => c.id));
      }
      return ids;
    });

    const allSelected = allGroupPermissionIds.every(id => 
      selectedPermissions.includes(id)
    );

    if (allSelected) {
      onChange(selectedPermissions.filter(id => !allGroupPermissionIds.includes(id)));
    } else {
      const newPermissions = [...selectedPermissions];
      allGroupPermissionIds.forEach(id => {
        if (!newPermissions.includes(id)) {
          newPermissions.push(id);
        }
      });
      onChange(newPermissions);
    }
  };

  const isGroupSelected = (groupPermissions: Permission[]): boolean => {
    const allGroupPermissionIds = groupPermissions.flatMap(p => {
      const ids = [p.id];
      if (p.children) {
        ids.push(...p.children.map(c => c.id));
      }
      return ids;
    });

    return allGroupPermissionIds.every(id => selectedPermissions.includes(id));
  };

  const isGroupPartiallySelected = (groupPermissions: Permission[]): boolean => {
    const allGroupPermissionIds = groupPermissions.flatMap(p => {
      const ids = [p.id];
      if (p.children) {
        ids.push(...p.children.map(c => c.id));
      }
      return ids;
    });

    const selectedCount = allGroupPermissionIds.filter(id => 
      selectedPermissions.includes(id)
    ).length;

    return selectedCount > 0 && selectedCount < allGroupPermissionIds.length;
  };

  return (
    <div className="space-y-6">
      {PERMISSION_GROUPS.map((group) => {
        const groupSelected = isGroupSelected(group.permissions);
        const groupPartial = isGroupPartiallySelected(group.permissions);

        return (
          <div key={group.id} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center mb-3">
              <input
                type="checkbox"
                id={`group-${group.id}`}
                checked={groupSelected}
                ref={(input) => {
                  if (input) {
                    input.indeterminate = groupPartial;
                  }
                }}
                onChange={() => handleGroupToggle(group.permissions)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label
                htmlFor={`group-${group.id}`}
                className="ml-3 text-sm font-semibold text-gray-900"
              >
                {group.label}
              </label>
            </div>

            <div className="ml-7 space-y-2">
              {group.permissions.map((permission) => (
                <div key={permission.id}>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id={permission.id}
                      checked={selectedPermissions.includes(permission.id)}
                      onChange={() => handlePermissionToggle(permission.id)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor={permission.id}
                      className="ml-3 text-sm text-gray-700"
                    >
                      {permission.label}
                    </label>
                  </div>

                  {permission.children && permission.children.length > 0 && (
                    <div className="ml-7 mt-2 space-y-2">
                      {permission.children.map((child) => (
                        <div key={child.id} className="flex items-center">
                          <input
                            type="checkbox"
                            id={child.id}
                            checked={selectedPermissions.includes(child.id)}
                            onChange={() => handlePermissionToggle(child.id)}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                          <label
                            htmlFor={child.id}
                            className="ml-3 text-sm text-gray-600"
                          >
                            {child.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PermissionCheckboxes;
